import { Directive, OnDestroy, OnInit } from '@angular/core';
import * as reactiveUtils from '@arcgis/core/core/reactiveUtils';
import { AbstractLegendInterface, JepLayer, Plugin } from '@jeppesen-foreflight/jmkw-types';
import { AbstractControlBar } from './abstract-control-bar';
import type { ResourceHandle } from "@arcgis/core/core/Handles";

/**
 * Abstract base class for legend components in the application.
 * Extends AbstractControlBar and provides common functionality for managing
 * plugin-based legends with ArcGIS layers.
 *
 * @abstract
 * @extends {AbstractControlBar}
 * @implements {OnInit}
 * @implements {OnDestroy}
 */
@Directive({})
export abstract class AbstractLegend
  extends AbstractControlBar
  implements AbstractLegendInterface, OnInit, OnDestroy
{
  /** Collection of watch handles for managing ArcGIS reactive utilities */
  readonly watchHandles = new Set<ResourceHandle>();

  /** Array of plugin IDs associated with this legend */
  pluginIDs?: string[];

  /** The primary plugin instance associated with this legend */
  plugin?: Plugin<JepLayer, string>;

  /** Collection of all ArcGIS layers associated with this legend */
  allLayers: any[] = [];

  constructor() {
    super();
  }

  /**
   * Adds a plugin ID to the list of plugin IDs associated with this legend.
   *
   * @param {string} pluginId The plugin ID to add
   */
  addPluginId(pluginId: string): void {
    if (!this.pluginIDs) {
      this.pluginIDs = [];
    }
    if (!this.pluginIDs.includes(pluginId)) {
      this.pluginIDs.push(pluginId);
    }
  }

  /**
   * Removes a plugin ID from the list of plugin IDs associated with this legend.
   *
   * @param {string} pluginId The plugin ID to remove
   */
  removePluginId(pluginId: string): void {
    if (this.pluginIDs) {
      this.pluginIDs = this.pluginIDs.filter((id) => id !== pluginId);
    }
  }

  /**
   * Retrieves the plugin instance associated with a specific plugin ID.
   *
   * @param {string} pluginId The plugin ID to search for
   * @returns {Plugin<JepLayer, string> | undefined} The plugin instance, if found
   */
  getPluginById(pluginId: string): Plugin<JepLayer, string> | undefined {
    return this.pluginIDs?.includes(pluginId)
      ? this.toolkitApiService.findPlugin({ id: pluginId }) || undefined
      : undefined;
  }

  /**
   * Updates the layers associated with this legend by re-extracting them
   * from the current plugin.
   */
  updateLayers(): void {
    if (this.plugin) {
      this.allLayers = this.extractAllLayers(this.plugin);
    }
  }

  /**
   * Lifecycle hook that initializes the plugin data when the component is created.
   * @inheritdoc
   */
  ngOnInit(): void {
    this.initializePluginData();
  }

  /**
   * Validates whether a plugin ID has been provided.
   *
   * @returns {boolean} True if the plugin ID is valid, false otherwise
   * @protected
   */
  validatePluginId(): boolean {
    if (!this.id) {
      console.warn('No plugin ID provided');
      return false;
    }
    return true;
  }

  /**
   * Initializes the plugin data by finding and setting up the associated plugin
   * and extracting its layers.
   *
   * @
   */
  initializePluginData(): void {
    if (!this.validatePluginId()) return;

    const pluginSearch = this.toolkitApiService.findPlugin({ id: this.id });
    if (!pluginSearch) return;

    this.plugin = pluginSearch;
    this.allLayers = this.extractAllLayers(this.plugin);
  }

  /**
   * Sets up a reactive watch on a source value with a callback.
   *
   * @template T The type of value being watched
   * @param {() => T} watchSource Function that returns the value to watch
   * @param {(value: T) => void} callback Function to execute when the watched value changes
   * @returns {ResourceHandle | null} Handle for the watch operation, or null if setup fails
   * @
   */
  setupWatch<T>(
    watchSource: () => T,
    callback: (value: T) => void
  ): ResourceHandle | null {
    return reactiveUtils.watch(watchSource, callback, { initial: true });
  }

  /**
   * Extracts all layers from a plugin and its children, including nested layers.
   *
   * @param {Plugin} plugin The plugin from which to extract layers
   * @returns {any[]} Array of all layers found in the plugin hierarchy
   * @
   */
  extractAllLayers(plugin: Plugin): any[] {
    const layers: any[] = [];
    if (plugin.layer) {
      layers.push(plugin.layer);
    }
    if (plugin.children?.allLayers) {
      layers.push(...plugin.children.allLayers());
    }
    return layers.flatMap((layer) =>
      layer?.['layers']?.items ? [...layers, ...layer['layers'].items] : layer
    );
  }

  /**
   * Lifecycle hook that performs cleanup when the component is destroyed.
   * Removes all watch handles to prevent memory leaks.
   * @inheritdoc
   */
  ngOnDestroy(): void {
    this.cleanupIHandles();
  }

  /**
   * Removes and cleans up all watch handles.
   *
   * @private
   */
  private cleanupIHandles(): void {
    this.watchHandles.forEach((handle) => {
      if (handle?.remove) {
        handle.remove();
      }
    });
    this.watchHandles.clear();
  }
}
